/**
 * AdminEventsPage - Admin dashboard for Events & Functions category
 * Features: Category settings, policy templates, audit logs, provider management
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Building, Utensils, Camera, Music, Palette, Package, ClipboardList,
  Settings, Shield, FileText, History, Users, TrendingUp, AlertCircle,
  Check, X, Edit, Plus, Trash2, Eye, Filter, Search, RefreshCw, Loader2,
  Globe, DollarSign, Clock, ChevronDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type configurations
const SERVICE_TYPES = [
  { slug: 'event-venues', name: 'Event Venues', icon: Building, color: 'purple' },
  { slug: 'catering-services', name: 'Catering Services', icon: Utensils, color: 'orange' },
  { slug: 'photography-videography', name: 'Photography & Videography', icon: Camera, color: 'pink' },
  { slug: 'djs-bands', name: 'DJs & Bands', icon: Music, color: 'cyan' },
  { slug: 'event-decoration', name: 'Event Decoration', icon: Palette, color: 'teal' },
  { slug: 'party-equipment-hire', name: 'Party Equipment Hire', icon: Package, color: 'amber' },
  { slug: 'event-planning', name: 'Event Planning', icon: ClipboardList, color: 'blue' }
];

// Stats Card Component
function StatCard({ title, value, icon: Icon, color = 'gray', subtitle }) {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
    gray: 'bg-gray-100 text-gray-600'
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Category Config Row Component
function CategoryConfigRow({ service, config, country, onUpdate }) {
  const Icon = service.icon;
  const [editing, setEditing] = useState(false);
  const [localConfig, setLocalConfig] = useState(config || {
    enabled: true,
    booking_mode: 'request',
    deposit_pct: 20,
    cancellation_hours: 72,
    verification_required: 1
  });

  const handleSave = () => {
    onUpdate(service.slug, localConfig);
    setEditing(false);
  };

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-${service.color}-100`}>
            <Icon className={`w-5 h-5 text-${service.color}-600`} />
          </div>
          <div>
            <h4 className="font-medium">{service.name}</h4>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={localConfig.enabled ? 'default' : 'secondary'} className="text-xs">
                {localConfig.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {localConfig.booking_mode}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {localConfig.deposit_pct}% deposit
              </Badge>
            </div>
          </div>
        </div>
        
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          <Edit className="w-4 h-4 mr-1" />
          Configure
        </Button>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              Configure {service.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Enable in {country}</Label>
              <Switch
                checked={localConfig.enabled}
                onCheckedChange={(checked) => setLocalConfig(c => ({ ...c, enabled: checked }))}
              />
            </div>

            <div>
              <Label>Booking Mode</Label>
              <Select 
                value={localConfig.booking_mode} 
                onValueChange={(v) => setLocalConfig(c => ({ ...c, booking_mode: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">Instant Booking</SelectItem>
                  <SelectItem value="request">Request Only</SelectItem>
                  <SelectItem value="quote">Quote First</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deposit Percentage</Label>
              <Input
                type="number"
                value={localConfig.deposit_pct}
                onChange={(e) => setLocalConfig(c => ({ ...c, deposit_pct: parseInt(e.target.value) }))}
                min={0}
                max={100}
              />
            </div>

            <div>
              <Label>Cancellation Window (hours)</Label>
              <Input
                type="number"
                value={localConfig.cancellation_hours}
                onChange={(e) => setLocalConfig(c => ({ ...c, cancellation_hours: parseInt(e.target.value) }))}
                min={0}
              />
            </div>

            <div>
              <Label>Minimum Verification Level</Label>
              <Select 
                value={localConfig.verification_required?.toString()} 
                onValueChange={(v) => setLocalConfig(c => ({ ...c, verification_required: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None Required</SelectItem>
                  <SelectItem value="1">Level 1 - Basic</SelectItem>
                  <SelectItem value="2">Level 2 - Verified</SelectItem>
                  <SelectItem value="3">Level 3 - Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Policy Template Editor Component
function PolicyTemplateEditor({ templates, country, onSave }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Policy Templates for {country}</h3>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-1" />
          New Template
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {templates.map((template, idx) => (
          <Card key={idx} className="hover:shadow-md transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium">{template.name}</h4>
                  <p className="text-sm text-gray-500 capitalize">{template.subcategory || 'All Categories'}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => {
                    setSelectedTemplate(template);
                    setEditModalOpen(true);
                  }}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Booking Mode</span>
                  <Badge variant="outline" className="capitalize">{template.booking_mode}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Deposit</span>
                  <span>{template.deposit_pct}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Cancellation Window</span>
                  <span>{template.cancellation_hours}h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">No-Show Policy</span>
                  <span className="capitalize">{template.no_show_policy?.replace(/_/g, ' ')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Audit Log Component
function AuditLogViewer({ logs }) {
  const actionColors = {
    issue_created: 'text-red-600 bg-red-50',
    resolution_offered: 'text-blue-600 bg-blue-50',
    resolution_accepted: 'text-green-600 bg-green-50',
    booking_confirmed: 'text-green-600 bg-green-50',
    booking_cancelled: 'text-red-600 bg-red-50',
    quote_sent: 'text-blue-600 bg-blue-50',
    config_updated: 'text-purple-600 bg-purple-50'
  };

  return (
    <div className="space-y-3">
      {logs.map((log, idx) => (
        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
          <div className={`px-2 py-1 rounded text-xs font-medium ${actionColors[log.action] || 'text-gray-600 bg-gray-100'}`}>
            {log.action?.replace(/_/g, ' ')}
          </div>
          <div className="flex-1">
            <p className="text-sm">{log.details}</p>
            <p className="text-xs text-gray-400 mt-1">
              {log.actor} • {log.timestamp && format(parseISO(log.timestamp), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
        </div>
      ))}
      {logs.length === 0 && (
        <p className="text-gray-500 text-center py-8">No audit logs to display</p>
      )}
    </div>
  );
}

// Main Admin Component
export default function AdminEventsPage() {
  const [country, setCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState({});
  const [categoryConfigs, setCategoryConfigs] = useState({});
  const [policyTemplates, setPolicyTemplates] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [issues, setIssues] = useState([]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch admin stats
        const statsRes = await fetch(`${API_URL}/api/events/admin/stats?country=${country}`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }

        // Fetch policy templates
        const templatesRes = await fetch(`${API_URL}/api/events/admin/policy-templates?country=${country}`);
        if (templatesRes.ok) {
          const templatesData = await templatesRes.json();
          setPolicyTemplates(templatesData.templates || []);
        }

        // Fetch providers
        const providersRes = await fetch(`${API_URL}/api/events/providers?country=${country}&limit=50`);
        if (providersRes.ok) {
          const providersData = await providersRes.json();
          setProviders(providersData.providers || []);
        }

        // Fetch issues
        const issuesRes = await fetch(`${API_URL}/api/events/issues?limit=20`);
        if (issuesRes.ok) {
          const issuesData = await issuesRes.json();
          setIssues(issuesData.issues || []);
        }

        // Mock audit logs
        setAuditLogs([
          { action: 'config_updated', actor: 'admin', details: 'Updated venue deposit to 25%', timestamp: new Date().toISOString() },
          { action: 'issue_created', actor: 'customer', details: 'New issue reported for booking #a1b2c3', timestamp: new Date().toISOString() },
          { action: 'resolution_offered', actor: 'provider', details: 'Offered partial refund', timestamp: new Date().toISOString() }
        ]);

      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [country]);

  // Handle category config update
  const handleConfigUpdate = async (slug, config) => {
    try {
      // API call to update config
      setCategoryConfigs(prev => ({ ...prev, [slug]: config }));
      toast.success(`${slug} configuration updated`);
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Settings className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Events & Functions Admin</h1>
                <p className="text-sm text-gray-500">Manage categories, policies, and providers</p>
              </div>
            </div>
            
            {/* Country Selector */}
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-gray-400" />
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                  <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="Total Providers"
            value={stats.total_providers || 0}
            icon={Users}
            color="purple"
          />
          <StatCard
            title="Total Bookings"
            value={stats.total_bookings || 0}
            icon={FileText}
            color="blue"
          />
          <StatCard
            title="Pending Bookings"
            value={stats.pending_bookings || 0}
            icon={Clock}
            color="orange"
          />
          <StatCard
            title="Open Issues"
            value={stats.open_issues || 0}
            icon={AlertCircle}
            color={stats.open_issues > 0 ? 'red' : 'green'}
          />
          <StatCard
            title="Revenue (Est)"
            value={country === 'UK' ? '£12,500' : 'Rs.850,000'}
            icon={TrendingUp}
            color="green"
            subtitle="This month"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="categories">Category Settings</TabsTrigger>
            <TabsTrigger value="policies">Policy Templates</TabsTrigger>
            <TabsTrigger value="providers">Providers</TabsTrigger>
            <TabsTrigger value="issues">
              Issues
              {stats.open_issues > 0 && (
                <Badge className="ml-2 bg-red-500">{stats.open_issues}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Providers by Type */}
              <Card>
                <CardHeader>
                  <CardTitle>Providers by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {SERVICE_TYPES.map(service => {
                      const count = stats.provider_counts?.[service.slug] || 0;
                      const Icon = service.icon;
                      return (
                        <div key={service.slug} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{service.name}</span>
                          </div>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Issues */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Issues</CardTitle>
                </CardHeader>
                <CardContent>
                  {issues.length > 0 ? (
                    <div className="space-y-3">
                      {issues.slice(0, 5).map((issue, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 border rounded-lg">
                          <div>
                            <p className="font-medium capitalize text-sm">{issue.issue_type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-gray-500">Booking #{issue.booking_id?.slice(0, 8)}</p>
                          </div>
                          <Badge className={
                            issue.status === 'open' ? 'bg-red-100 text-red-700' :
                            issue.status === 'resolution_offered' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {issue.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent issues</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Category Configuration</CardTitle>
                <CardDescription>
                  Configure booking modes, deposits, and requirements for each service type in {country}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {SERVICE_TYPES.map(service => (
                    <CategoryConfigRow
                      key={service.slug}
                      service={service}
                      config={categoryConfigs[service.slug]}
                      country={country}
                      onUpdate={handleConfigUpdate}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies">
            <Card>
              <CardHeader>
                <CardTitle>Policy Templates</CardTitle>
                <CardDescription>
                  Default cancellation, refund, and booking policies for providers
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PolicyTemplateEditor
                  templates={policyTemplates}
                  country={country}
                  onSave={(template) => {
                    setPolicyTemplates(prev => [...prev, template]);
                    toast.success('Template saved');
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Service Providers</CardTitle>
                    <CardDescription>{providers.length} providers in {country}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input placeholder="Search providers..." className="pl-9 w-64" />
                    </div>
                    <Select defaultValue="all">
                      <SelectTrigger className="w-40">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {SERVICE_TYPES.map(s => (
                          <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Provider</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Type</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">City</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Rating</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Verification</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-gray-500">Status</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map(provider => (
                        <tr key={provider.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="font-medium">{provider.provider_name}</div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="capitalize text-xs">
                              {provider.service_type?.replace(/-/g, ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-sm text-gray-600">{provider.city}</td>
                          <td className="py-3 px-2">
                            <span className="flex items-center gap-1">
                              ⭐ {provider.rating?.toFixed(1)}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={
                              provider.verification_level >= 3 ? 'bg-green-100 text-green-700' :
                              provider.verification_level >= 2 ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              Level {provider.verification_level || 0}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={provider.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {provider.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues">
            <Card>
              <CardHeader>
                <CardTitle>Customer Issues</CardTitle>
                <CardDescription>Review and monitor dispute resolutions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {issues.map((issue, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium capitalize">{issue.issue_type?.replace(/_/g, ' ')}</h4>
                          <p className="text-sm text-gray-500">
                            Booking #{issue.booking_id?.slice(0, 8)} • Provider #{issue.provider_id?.slice(0, 8)}
                          </p>
                        </div>
                        <Badge className={
                          issue.status === 'open' ? 'bg-red-100 text-red-700' :
                          issue.status === 'resolution_offered' ? 'bg-blue-100 text-blue-700' :
                          issue.status === 'resolved' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }>
                          {issue.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{issue.description}</p>
                      
                      {issue.resolution && (
                        <div className="p-2 bg-blue-50 rounded text-sm mb-3">
                          <strong>Resolution Offered:</strong> {issue.resolution.type?.replace(/_/g, ' ')}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">View Details</Button>
                        {issue.status === 'open' && (
                          <Button size="sm" variant="outline" className="text-orange-600">
                            Escalate
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {issues.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No issues to display</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Audit Log</CardTitle>
                    <CardDescription>Track all system actions and changes</CardDescription>
                  </div>
                  <Button variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <AuditLogViewer logs={auditLogs} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
